/**
 * YouTube embed helpers — 1:1 port of legacy
 * `flows/bio/utils.ts` `extractYoutubeVideoId` and
 * `flows/bio/pages/home/components/VideoBackground.shared.tsx`
 * `generateYoutubeEmbed`.
 *
 * Kept verbatim so any future legacy changes can be re-applied
 * without touching the call sites.
 */

/**
 * Pulls the canonical 11-char video id out of any YouTube URL form
 * (watch / youtu.be / embed / shorts / /v/ / live). Returns `null`
 * for unrecognized hostnames.
 *
 * NOTE: unlike legacy `flows/bio/utils.ts:418` (which returned `null`
 * for `/live/` URLs), we DO extract the id from a live URL — a
 * `youtube.com/live/<id>` link carries a normal 11-char id that
 * embeds via `/embed/<id>` just like any VOD, so rejecting it only
 * blocked a valid video (creators paste the "share" link of a live
 * stream / its replay). {@link normalizeYoutubeUrl} rewrites it to
 * the canonical `/watch?v=<id>` form for downstream scrape/storage.
 */
export function extractYoutubeVideoId(url: string): string | null {
  if (!url) return null;
  try {
    const urlObj = new URL(url.trim());
    const hostname = urlObj.hostname;

    if (!['youtube.com', 'www.youtube.com', 'youtu.be'].includes(hostname)) {
      return null;
    }

    if (hostname === 'youtu.be') {
      return urlObj.pathname.slice(1) || null;
    }

    if (urlObj.pathname === '/watch') {
      return urlObj.searchParams.get('v');
    }

    if (urlObj.pathname.startsWith('/embed/')) {
      return urlObj.pathname.split('/')[2] || null;
    }

    if (urlObj.pathname.startsWith('/shorts/')) {
      return urlObj.pathname.split('/')[2] || null;
    }

    if (urlObj.pathname.startsWith('/v/')) {
      return urlObj.pathname.split('/')[2] || null;
    }

    if (urlObj.pathname.startsWith('/live/')) {
      return urlObj.pathname.split('/')[2] || null;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Rewrite any recognized YouTube URL form (live / youtu.be / embed /
 * shorts / /v/) to the canonical `https://www.youtube.com/watch?v=<id>`.
 * Returns the input unchanged when it isn't a recognizable YouTube
 * video URL (so non-YouTube URLs and already-canonical `/watch` links
 * pass through untouched).
 *
 * Used at the video-button URL input so the stored value, the music/
 * video scrape, and the embed all receive a normal watch URL — the
 * scrape endpoint 404s on a bare `/live/<id>` link, and canonicalizing
 * first fixes that without special-casing every consumer.
 */
export function normalizeYoutubeUrl(url: string): string {
  const id = extractYoutubeVideoId(url);
  if (id == null) return url;
  return `https://www.youtube.com/watch?v=${id}`;
}

interface YouTubeEmbedOptions {
  width?: number;
  height?: number;
  autoplay?: boolean;
  mute?: boolean;
  controls?: boolean;
  showInfo?: boolean;
  rel?: boolean;
  modestBranding?: boolean;
  start?: number;
  end?: number;
  loop?: boolean;
  zoom?: number;
}

/**
 * Build the embed URL + iframe-style props for a YouTube video.
 * Mirrors legacy `generateYoutubeEmbed` defaults exactly:
 *   width=560 / height=315 / loop=true / autoplay=false / mute=false
 *   controls=true / showInfo=true / rel=true / modestBranding=false
 *
 * Returns the parts a React `<iframe>` can consume directly
 * (rather than a string of HTML — the React port doesn't need
 * `dangerouslySetInnerHTML`).
 */
export function buildYoutubeEmbed(
  videoId: string,
  options: YouTubeEmbedOptions = {},
): {
  src: string;
  width: number;
  height: number;
  /**
   * Style for the OUTER wrapper that carries the zoom crop. Laid out at
   * `100% / zoom` and scaled back up so the net footprint is exactly 100% of
   * the button. 1:1 with legacy `generateYoutubeEmbed`'s wrapper div.
   */
  wrapperStyle: React.CSSProperties;
  /**
   * Style for the iframe itself. Uses the `width:0; min-width:100%` trick
   * (NOT a bare `width:100%`) so iOS Safari actually stretches the iframe to
   * fill the wrapper — mobile Safari ignores a percentage `width` on an
   * `<iframe>` but honors `min-width`. 1:1 with legacy.
   */
  iframeStyle: React.CSSProperties;
} {
  const {
    width = 560,
    height = 315,
    loop = true,
    autoplay = false,
    mute = false,
    controls = true,
    showInfo = true,
    rel = true,
    modestBranding = false,
    start,
    end,
    zoom = 1.0,
  } = options;

  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    mute: mute ? '1' : '0',
    controls: controls ? '1' : '0',
    showinfo: showInfo ? '1' : '0',
    rel: rel ? '1' : '0',
    modestbranding: modestBranding ? '1' : '0',
  });

  if (loop) {
    params.append('loop', '1');
    // Required for single-video loop on YouTube embeds.
    params.append('playlist', videoId);
  }

  if (typeof start === 'number' && start > 0) {
    params.append('start', String(start));
  }
  if (typeof end === 'number' && typeof start === 'number' && end > start) {
    params.append('end', String(end));
  }

  return {
    src: `https://www.youtube.com/embed/${videoId}?${params.toString()}`,
    width,
    height,
    // Zoom-out crop, 1:1 with legacy `VideoBackground.shared.tsx:81`. Two
    // elements, NOT one: the WRAPPER is laid out at `100% / zoom` and scaled
    // back up (net footprint = 100% of the button, YouTube chrome cropped);
    // the IFRAME fills the wrapper via the `width:0; min-width:100%` trick.
    // Putting the zoom transform + a `width:calc(100%/zoom)` on the iframe
    // ITSELF renders small on iOS Safari — mobile Safari ignores a percentage
    // width on an `<iframe>`, so it fell back to the intrinsic 560×315 (QA,
    // twice: desktop white-gutter + iOS small-video — keep this two-element
    // form on rebases).
    wrapperStyle: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: zoom !== 1 ? `calc(100% / ${zoom})` : '100%',
      height: zoom !== 1 ? `calc(100% / ${zoom})` : '100%',
      ...(zoom !== 1
        ? { transform: `scale(${zoom})`, transformOrigin: '0 0' }
        : null),
    },
    iframeStyle: {
      width: 0,
      height: 0,
      minWidth: '100%',
      maxWidth: '100%',
      minHeight: '100%',
      maxHeight: '100%',
      border: 0,
    },
  };
}
